import { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { last } from "lodash"
import classNames from "classnames"
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium"
import { Box, Stack, Typography } from "@mui/material"
import { ErrorBoundary } from "@sentry/react"
import { useControllableState } from "hooks/use-controllable-state"
import { ASC, DESC } from "helpers/sorts"
import { LOADING, ERROR } from "helpers/data-statuses"
import { MetricPropType } from "./prop-types"
import { useContributorsGridBase, useTableData, useColumnDefinitions, useColumnDragImage, useCellValueFlash, useTradesResetKey } from "./hooks"
import { TableFooter, TableHeader, LabelCell, MetricCell, DataGridErrorFallback, CustomColumnMenu } from "./components"
import DrilldownToolbar from "./components/drilldown-toolbar"
import { DEFAULT_METRIC_COLUMN_WIDTH, MAX_COLUMN_WIDTH } from "./constants"
import { getVisibleIntradayPricePrefix, isRowPricedOnPriorClose } from "./helpers/intraday-prior-row"

// Columns to skip when picking the auto-scroll target after remount. Descriptors (NMV, %, sector…)
// plus the performance contribution returns (Total/Specific/Factor/Trading/Residual) — which sort
// before per-factor columns — so the panel scrolls straight to the first factor-specific column.
// startsWith covers comparison/delta suffixed variants (e.g. totalReturnComparison).
const SCROLL_SKIP_PREFIXES = [
  "label", "description", "bloombergTicker",
  "marketValue", "percentEquity", "sector", "country", "averagePercentEquity",
  "totalReturn", "specificReturn", "factorsReturn", "tradingReturn", "residualReturn",
]

const EMPTY_ARRAY = []

function noopOnChange() {
  return undefined
}

// Shown in place of the empty-grid skeleton when an intraday performance drilldown is opened in a
// live-pricing session with no committed batch — per-asset contributors only exist for a batch.
export function IntradayContributorsUnavailableOverlay() {
  return (
    <Stack
      role="status"
      data-test-class="intraday-contributors-unavailable"
      sx={{
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        px: 3,
        textAlign: "center",
      }}
    >
      <Typography
        variant="body2"
        sx={{
        color: "text.secondary",
      }}
      >
        Per-asset contributors aren&apos;t available for live intraday pricing yet. They appear once an
        intraday batch is committed — refresh to load the latest data when it becomes available.
      </Typography>
    </Stack>
  )
}

function DrilldownContributorsDataGridComponent({
  status,
  data,
  metrics,
  disableCategories,
  groupByName,
  idLabel,
  classes,
  viewLabel,
  currentFactor,
  factorOptions,
  filterFactorOptions,
  onFactorChange,
  frozenSnapshot,
  selectedFactor,
  selectedCategory,
  defaultSort,
  isIntraday,
  intradayAsOf,
  hasNewerBatch,
  intradayContributorsUnavailable,
  intradayPricePrefix = null,
  onRefresh,
  groupBy,
  onGroupByChange,
  availableGroupByOptions,
  isExpanded,
  onExpansionToggle,
  sortMetricId: propSortMetricId,
  onSortMetricIdChange,
  sortDirection: propSortDirection,
  onSortDirectionChange,
  tableNavigationLevels: propTableLevels,
  onTableNavigationChange,
  onSortChanged,
  onTableNavigationChanged,
  onRowClicked,
}) {
  // Sort follows defaultSort — which updates as metrics finish loading and the selected factor
  // changes — until the user picks a column (or a controlling parent supplies sort props).
  // A manual sort is tagged with the factor it was made under, so switching factors falls back
  // to the new factor's default instead of sorting on a column that may not exist for it.
  const [userSort, setUserSort] = useState(null)
  const userSortActive = userSort !== null && userSort.factor === currentFactor

  const sortMetricId = propSortMetricId !== undefined
    ? propSortMetricId
    : (userSortActive ? userSort.metricId : (defaultSort?.path ?? null))
  const sortDirection = propSortDirection !== undefined
    ? propSortDirection
    : (userSortActive ? userSort.direction : (defaultSort?.direction ?? DESC))

  const [selectedTableLevels, setSelectedTableLevels] = useControllableState(
    propTableLevels,
    [],
    onTableNavigationChange ?? noopOnChange,
  )

  const effectiveGroupByName = groupByName ?? groupBy?.label ?? undefined

  const handleSortChange = useCallback((field, direction) => {
    const nextMetricId = direction === null ? null : field
    setUserSort({ metricId: nextMetricId, direction, factor: currentFactor })
    onSortMetricIdChange?.(nextMetricId)
    onSortDirectionChange?.(direction)
    onSortChanged?.(nextMetricId, direction)
  }, [currentFactor, onSortMetricIdChange, onSortDirectionChange, onSortChanged])

  const tableDataProps = {
    data,
    metrics,
    disableCategories,
    selectedTableLevels,
    setSelectedTableLevels,
    sortMetricId,
    sortDirection,
    highlightedRowIds: [],
    onTableNavigationChanged,
    onRowClicked,
    columnOrder: "custom",
    status,
  }

  const {
    currentLevelData,
    parentLevelData,
    metricMetadata,
    metricCategories,
    sortedContributorsArray,
    rows,
    pinnedRows,
    isTopLevel,
    currentLevelLabel,
    sortArrowDirection,
    isAbsoluteSort,
    handleSetTableLevel,
    handleClickContributor,
  } = useTableData(tableDataProps)

  const visibleIntradayPricePrefix = useMemo(() => getVisibleIntradayPricePrefix(metricMetadata), [metricMetadata])

  const columnProps = useMemo(() => ({
    idLabel,
    metricMetadata,
    metricCategories,
    groupingEnabled: true,
    customGroups: [],
    disableCategories,
    dropdownTableTitle: effectiveGroupByName,
    rootData: data,
    savedCustomColumnOrder: undefined,
    savedDataGridState: undefined,
    LabelCellComponent: LabelCell,
    intradayPricePrefix,
    MetricCellComponent: MetricCell,
    CustomHeaderComponent: TableHeader,
    columnOrder: "custom",
    sortMetricId,
    sortDirection,
    isAbsoluteSort,
    sortArrowDirection,
    handleClickContributor,
    includeDrilldownArrows: false,
    isTopLevel,
    handleSetTableLevel,
    onSortChange: handleSortChange,
    parentLevelOptions: parentLevelData?.children || {},
    currentGroupId: last(selectedTableLevels),
    data: currentLevelData,
    sortedContributorsArray,
  }), [
    idLabel, metricMetadata, metricCategories, disableCategories, effectiveGroupByName, data,
    sortMetricId, sortDirection, isAbsoluteSort, sortArrowDirection, handleClickContributor,
    isTopLevel, handleSetTableLevel, handleSortChange, parentLevelData,
    selectedTableLevels, currentLevelData, sortedContributorsArray, intradayPricePrefix,
  ])

  const apiRef = useGridApiRef()
  // Track which dataGridRemountKey has completed autosize+scroll. Comparing this to
  // the current key is synchronous (no state-update lag), so the DataGrid stays hidden
  // from the very first render of a new key until the scroll is ready to show.
  const [scrollReadyKey, setScrollReadyKey] = useState(null)

  const { columns, columnGroupingModel } = useColumnDefinitions(columnProps)

  const { fieldExtractors, dataGridRemountKey } = useContributorsGridBase({ columns, rows })

  useColumnDragImage(apiRef)

  // Factor filtering — which sibling factors actually have exposure in the loaded data —
  // is computed by the orchestrator (drilldown-asset-view) which owns the domain knowledge
  // about which value-set prefixes represent non-factor views.
  const displayedFactorOptions = useMemo(() =>
    filterFactorOptions ? filterFactorOptions(factorOptions, data) : factorOptions,
  [filterFactorOptions, factorOptions, data],
  )

  useEffect(() => {
    const key = dataGridRemountKey
    const api = apiRef.current
    let cancelled = false
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return
      if (!api) {
        setScrollReadyKey(key)
        return
      }
      const isSkipped = (col) => SCROLL_SKIP_PREFIXES.some(p => col.field.startsWith(p))

      const allCols = api.getAllColumns()
      const targetCol = allCols.find(col => !col.isPinned && !isSkipped(col))

      // Autosize first — widths change column positions, so scroll must run after.
      api.autosizeColumns({
        includeHeaders: true,
        includeOutliers: false,
        minWidth: DEFAULT_METRIC_COLUMN_WIDTH,
        maxWidth: MAX_COLUMN_WIDTH,
      }).then(() => {
        if (cancelled) return
        if (targetCol) {
          // Left-align the target column after autosize so widths are final.
          // getColumnPosition returns the offset from the full column list (including pinned),
          // so subtract leftPinnedWidth to get the virtual-scroller coordinate.
          const leftPos = api.getColumnPosition(targetCol.field)
          const { leftPinnedWidth = 0 } = api.getRootDimensions()
          api.scroll({ left: Math.max(0, leftPos - leftPinnedWidth) })
        }
        setScrollReadyKey(key)
      }).catch(() => {
        if (!cancelled) setScrollReadyKey(key)
      })
    })
    return () => { cancelled = true; cancelAnimationFrame(rafId) }
  }, [dataGridRemountKey, apiRef, setScrollReadyKey])

  const getRowClassName = useCallback((params) => {
    const contributor = params.row.contributor
    const useDataZebraStriping = !contributor.notHeld
      && !contributor.isNoLongerHeld
      && !contributor.pinnedRowOrder
    const zebraStripeIsAlt = useDataZebraStriping && params.indexRelativeToCurrentPage % 2 === 1

    return classNames({
      "not-held": contributor.notHeld,
      "no-longer-held": contributor.isNoLongerHeld,
      pinned: contributor.pinnedRowOrder,
      unmodeled: contributor.isUnmodeled,
      "aggregation-row": params.row.isAggregation,
      "drill-down": params.row.isAggregation && !isTopLevel,
      "sticky-aggregation": params.row.isAggregation,
      "row-zebra-base": useDataZebraStriping && !zebraStripeIsAlt,
      "row-zebra-alt": zebraStripeIsAlt,
      "intraday-price-prior": isRowPricedOnPriorClose(params, visibleIntradayPricePrefix),
    })
  }, [isTopLevel, visibleIntradayPricePrefix])

  const loading = status === LOADING
  const getCellClassName = useCellValueFlash(rows, loading, fieldExtractors)

  return (
    <Box className={classNames("contributors-datagrid", "contributors-datagrid--drilldown", classes)}>
      <DrilldownToolbar
        viewLabel={viewLabel}
        currentFactor={currentFactor}
        factorOptions={displayedFactorOptions}
        onFactorChange={onFactorChange}
        frozenSnapshot={frozenSnapshot}
        selectedFactor={selectedFactor}
        selectedCategory={selectedCategory}
        isIntraday={isIntraday}
        intradayAsOf={intradayAsOf}
        hasNewerBatch={hasNewerBatch}
        intradayContributorsUnavailable={intradayContributorsUnavailable}
        onRefresh={onRefresh}
        groupBy={groupBy}
        onGroupByChange={onGroupByChange}
        availableGroupByOptions={availableGroupByOptions ?? EMPTY_ARRAY}
        isExpanded={isExpanded}
        onExpansionToggle={onExpansionToggle}
      />
      <DataGridPremium
        key={dataGridRemountKey}
        sx={{ borderRadius: 0, visibility: scrollReadyKey === dataGridRemountKey ? "visible" : "hidden" }}
        initialState={{
          density: "compact",
          pinnedColumns: { left: ["label", "description"] },
        }}
        autosizeOptions={{
          includeOutliers: false,
          includeHeaders: true,
          minWidth: DEFAULT_METRIC_COLUMN_WIDTH,
          maxWidth: MAX_COLUMN_WIDTH,
        }}
        apiRef={apiRef}
        loading={loading}
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        pinnedRows={pinnedRows}
        sortingMode="server"
        columnResizeMode="onEnd"
        scrollbarSize={17}
        rowHeight={40}
        slots={{
          footer: TableFooter,
          columnMenu: CustomColumnMenu,
          ...(intradayContributorsUnavailable && { noRowsOverlay: IntradayContributorsUnavailableOverlay }),
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton",
          },
          toolbar: {
            isTopLevel,
            currentLevelLabel,
            handleSetTableLevel,
          },
          columnMenu: {
            onSortChange: handleSortChange,
            currentSortField: sortMetricId,
            currentSortDirection: sortDirection,
          },
          basePopper: {
            className: "contributors-datagrid-menu-popper",
          },
        }}
        getRowClassName={getRowClassName}
        getCellClassName={getCellClassName}
        hideFooterSelectedRowCount
        hideFooterRowCount={false}
        cellModesModel={{}}
        columnBufferPx={0}
        checkboxSelection={false}
        disableVirtualization={false}
        disableColumnResize={false}
        disableRowGrouping
        disableAggregation
        disableColumnSelector={false}
        disableDensitySelector={false}
        disableMultipleColumnsFiltering={false}
        disableExtendRowFullWidth
        disableMultipleRowSelection
        disableRowSelectionOnClick
        disableColumnSorting
      />
    </Box>
  )
}

function DrilldownContributorsDataGridWithError({ status, errorMessage, ...props }) {
  if (status === ERROR) {
    throw new Error(errorMessage ?? "")
  }

  return <DrilldownContributorsDataGridComponent status={status} {...props} />
}

DrilldownContributorsDataGridWithError.propTypes = {
  status: PropTypes.oneOf([LOADING, ERROR, "success"]),
  errorMessage: PropTypes.string,
}

export default function DrilldownContributorsDataGrid({ status, ...props }) {
  const tradesResetKey = useTradesResetKey()

  return (
    <ErrorBoundary key={tradesResetKey} fallback={DataGridErrorFallback}>
      <DrilldownContributorsDataGridWithError status={status} {...props} />
    </ErrorBoundary>
  )
}

DrilldownContributorsDataGrid.propTypes = {
  data: PropTypes.object.isRequired,
  status: PropTypes.oneOf([LOADING, ERROR, "success"]).isRequired,
  errorMessage: PropTypes.string,
  metrics: PropTypes.arrayOf(MetricPropType).isRequired,
  disableCategories: PropTypes.bool,
  groupByName: PropTypes.string,
  idLabel: PropTypes.string,
  classes: PropTypes.string,
  viewLabel: PropTypes.string,
  currentFactor: PropTypes.string,
  factorOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, primaryText: PropTypes.string.isRequired })),
  filterFactorOptions: PropTypes.func,
  onFactorChange: PropTypes.func,
  defaultSort: PropTypes.shape({
    path: PropTypes.string,
    direction: PropTypes.oneOf([ASC, DESC]),
  }),
  frozenSnapshot: PropTypes.object,
  selectedFactor: PropTypes.string,
  selectedCategory: PropTypes.string,
  isIntraday: PropTypes.bool,
  intradayAsOf: PropTypes.string,
  hasNewerBatch: PropTypes.bool,
  intradayContributorsUnavailable: PropTypes.bool,
  intradayPricePrefix: PropTypes.string,
  onRefresh: PropTypes.func,
  groupBy: PropTypes.object,
  onGroupByChange: PropTypes.func,
  availableGroupByOptions: PropTypes.arrayOf(PropTypes.object),
  isExpanded: PropTypes.bool,
  onExpansionToggle: PropTypes.func,
  sortMetricId: PropTypes.string,
  onSortMetricIdChange: PropTypes.func,
  sortDirection: PropTypes.oneOf([ASC, DESC, "ASC_ABS", "DESC_ABS", null]),
  onSortDirectionChange: PropTypes.func,
  tableNavigationLevels: PropTypes.arrayOf(PropTypes.string),
  onTableNavigationChange: PropTypes.func,
  onSortChanged: PropTypes.func,
  onTableNavigationChanged: PropTypes.func,
  onRowClicked: PropTypes.func,
}

DrilldownContributorsDataGridComponent.propTypes = DrilldownContributorsDataGrid.propTypes
